<template>
  <div
    v-if="displayComputed"
    class="product-promoted-campaign critical-component-hide"
  >
    <div v-if="showOnlyGraduate" class="product-promoted-graduated">
      <p
        class="product-promoted-graduated__title text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-html="$string('discount_campaigns.graduated_discount.solo_html')"
      />

      <div class="product-promoted-graduated__wrapper">
        <template
          v-for="string of graduatedDiscountOnly"
          :key="`graduated-discount-${string.text}`"
        >
          <span
            class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-html="string.text"
          />
          <span
            class="product-promoted-graduated__percentage text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-text="string.percentage"
          />
        </template>
      </div>
    </div>

    <div v-else class="product-promoted-campaign__header">
      <p
        class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
        v-html="getPromotedCampaignTitle(true)"
      />

      <button
        class="product-promoted-campaign__code"
        type="button"
        @click="handleCopy(false)"
      >
        <icon-copy />

        <span
          class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
          v-text="promotedCampaignCode"
        />

        <span
          class="tooltip text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          :class="{ [$class('active')]: showTooltip }"
          v-text="$string('general.hero_teaser.tooltip_text')"
        />
      </button>
    </div>

    <template v-if="!showOnlyGraduate">
      <p
        class="product-promoted-campaign__graduated text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-html="graduatedDiscountText"
      />

      <p
        class="product-promoted-campaign__content text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-text="$string('discount_campaigns.promoted_campaign.content')"
      />

      <product-prices
        class="product-promoted-campaign__prices-container"
        :compare-at-price="variant.compareAtPrice?.amount"
        :discount-logic="discountLogic"
        :price="variant.price?.amount"
        template="promoted"
        use-promoted-campaign
        :hide-price-show-saving="hidePromoPrice"
      />

      <button
        class="product-promoted-campaign__learn-more text-mobile-paragraph-s-regular-link text-desktop-paragraph-s-regular-link"
        type="button"
        @click="handleOpenOverlay"
        v-text="$string('discount_campaigns.promoted_campaign.learn_more')"
      />
    </template>

    <!-- Learn more modal -->
    <teleport to="body">
      <overlay
        key="discountCampaignsPromotedModal"
        block-class="discount-campaigns-overlay"
        namespace="discountCampaignsPromotedModal"
        direction="up"
        :show-close="false"
        type="modal"
      >
        <template #body>
          <div class="discount-campaigns__header">
            <button
              class="discount-campaigns__close icon-button"
              type="button"
              @click="handleCloseOverlay"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.close')"
              />

              <icon-close />
            </button>

            <p
              class="discount-campaigns__title text-mobile-headlines-h4 text-desktop-headlines-h4"
              v-html="getPromotedCampaignTitle()"
            />

            <button
              class="product-promoted-campaign__code product-promoted-campaign__code--modal"
              type="button"
              @click="handleCopy(true)"
            >
              <icon-copy />

              <span
                class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
                v-text="promotedCampaignCode"
              />

              <span
                class="tooltip text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
                :class="{ [$class('active')]: showModalTooltip }"
                v-text="$string('general.hero_teaser.tooltip_text')"
              />
            </button>
          </div>

          <div
            class="discount-campaigns__content product-promoted-campaign__modal-content"
          >
            <p
              class="text-center text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
              v-html="promotedCampaignModalContent"
            />

            <product-prices
              class="product-promoted-campaign__modal-prices-container"
              :compare-at-price="variant.compareAtPrice?.amount"
              :discount-logic="discountLogic"
              :price="variant.price?.amount"
              use-promoted-campaign
              template="promoted-modal"
              :hide-price-show-saving="hidePromoPrice"
            />

            <btn
              class="product-promoted-campaign__add-to-cart"
              :disabled="!variant.available"
              :label="addToCartText"
              modifiers="cta block center"
              :show-state-icon="state === 'adding'"
              show-icon
              type="button"
              @click="handleAddToCart"
            >
              <template #icon>
                <icon-add />
              </template>

              <template #stateIcon>
                <icon-loading />
              </template>
            </btn>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: Product promoted campaign (product-promoted-campaign)
 * -----------------------------------------------------------------------------
 * Promotes campaign on applicable products.
 *
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {String} state - Add to cart state.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits addToCart
 *
 */
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import Btn from '~global/btn/btn';
import Overlay from '~global/overlay/overlay';
import ProductPrices from '~global/product-prices/product-prices';

import IconAdd from '~icons/general/add.svg';
import IconClose from '~icons/directional-navigation/close.svg';
import IconCopy from '~icons/general/copy.svg';
import IconLoading from '~icons/general/loading.svg';

export default {
  name: 'ProductPromotedCampaign',

  components: {
    Btn,
    IconAdd,
    IconClose,
    IconCopy,
    IconLoading,
    Overlay,
    ProductPrices,
  },

  props: {
    discountLogic: {
      type: Object,
      default: () => ({
        collectionIds: [],
        discountPreview: false,
        onSale: false,
        productId: 0,
        variantId: 0,
      }),
    },
    state: {
      type: String,
      default: 'ready',
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'add-to-cart': () => true,
  },

  data() {
    return {
      showTooltip: false,
      showModalTooltip: false,
      graduatedPercentage: [],
      graduatedQuantity: [],
      currentCampaign: [],
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      promotedCampaign: 'promoted',
      activeCampaign: 'active',
    }),

    /**
     * Compute display behaviour
     * separated from display method as the update needs to happen
     * after code is applied and called
     */
    displayComputed() {
      if (!this.graduatedPercentage) {
        this.checkVariantApplied();
      }

      if (this.graduatedPercentage.length > 0 && this.activeCampaign) {
        return this.activeCampaign && !this.promotedCampaign;
      }
      return this.promotedCampaign && this.discount;
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant) {
        return this.$string('product.cta.select_variant');
      }

      if (!this.variant.available) {
        return this.$string('product.cta.notify_me');
      }

      return this.$string('product.cta.add_to_cart');
    },

    /**
     * Compute current campaign metaobject.
     * @returns {Boolean|Object}
     */
    currentCampaignMetaobject() {
      if (!this.currentCampaign) {
        return false;
      }

      return this.$variable('discount.metaobjects').find((object) => {
        return object.campaignId === this.currentCampaign.campaignId;
      });
    },

    /**
     * Computed display if there are only graduate discounts and no
     * campaign discount
     * @returns {Boolean}
     */
    showOnlyGraduate() {
      // if no promotion campaign but graduated discount
      if (this.currentCampaign?.graduatedDiscounts && this.discount === 0) {
        return this.graduatedPercentage.length > 0;
      }

      return false;
    },

    /**
     * Hide Pride in Promo Box for specific markets
     * @returns {Boolean}
     */
    hidePromoPrice() {
      return this.$variable('store.marketPromoPriceHide');
    },

    /**
     * Compute applicable discount.
     * @returns {Number}
     */
    discount() {
      return this.getCampaignDiscount(this.discountLogic, 'promoted');
    },

    /**
     * Compute promoted campaign code.
     * @returns {String}
     */
    promotedCampaignCode() {
      if (!this.promotedCampaign || !this.promotedCampaign.code?.length) {
        return '';
      }

      return this.promotedCampaign.code[0];
    },

    graduatedDiscountOnly() {
      return this.handleGraduatedDiscountOnly();
    },

    /**
     * Compute graduated discount
     * Checks if variant is part of the graduated discount
     * exchanges between different translation strings
     * @returns {String}
     */
    graduatedDiscountText() {
      if (!this.currentCampaign.graduatedDiscounts) {
        return '';
      }
      let string = '';

      // if none of the tiers have the variant,
      // then the variant is not part of the graduated discount
      if (
        this.graduatedPercentage.length === 0 &&
        this.graduatedQuantity.length === 0
      ) {
        return '';
      }

      // if more than one graduated discount
      if (this.graduatedPercentage.length > 1) {
        for (let index = 0; index < this.graduatedPercentage.length; index++) {
          if (index === 0) {
            string += this.$string(
              'discount_campaigns.graduated_discount.several_first_html',
              {
                replace: {
                  amount: this.graduatedQuantity[index],
                  percentage: this.graduatedPercentage[index],
                },
              },
            );
          } else {
            string += ` ${this.$string(
              'discount_campaigns.graduated_discount.several_next_html',
              {
                replace: {
                  amount: this.graduatedQuantity[index],
                  percentage: this.graduatedPercentage[index],
                },
              },
            )}`;
          }

          if (index === this.graduatedPercentage.length - 1) {
            string += '.';
          }
        }
      } else {
        string = this.$string(
          'discount_campaigns.graduated_discount.single_html',
          {
            replace: {
              percentage: this.graduatedPercentage,
              amount: this.graduatedQuantity,
            },
          },
        );
      }

      return string;
    },

    /**
     * Compute promoted campaign modal content.
     * @returns {String}
     */
    promotedCampaignModalContent() {
      if (!this.promotedCampaign) {
        return '';
      }

      let startDate = false;
      let startDateString = '';

      if (this.promotedCampaign.startDate) {
        startDate = new Date(this.promotedCampaign.startDate);
        startDateString = this.$formatDate(
          'date_formats.campaign_start',
          startDate,
        );
      }

      let string =
        this.hidePromoPrice === false
          ? this.$string(
              'discount_campaigns.promoted_campaign.modal_content_price_html',
              {
                replace: { start_date: startDateString },
              },
            )
          : this.$string(
              'discount_campaigns.promoted_campaign.modal_content_saving_html',
              {
                replace: { start_date: startDateString },
              },
            );

      if (this.currentCampaignMetaobject?.conditions) {
        string = `${this.currentCampaignMetaobject?.conditions} ${string}`;
      }

      return string
        ?.replace(
          '<strong',
          '<strong class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"',
        )
        ?.replace(
          '<p',
          '<p class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"',
        );
    },
  },

  watch: {
    // watch the variant to check if graduated discount is there
    variant() {
      // reset as variant changes
      this.graduatedPercentage = [];
      this.graduatedQuantity = [];

      // check for graduatedDiscounts
      this.checkVariantApplied();
    },

    // watch discount to react on it
    discount() {
      this.display();
    },
  },

  mounted() {
    // check for graduatedDiscounts
    this.checkVariantApplied();
  },

  methods: {
    /**
     *  Compute if the component should be displayed.
     * - Check if graduated discounts are active and variant is included
     * - Then determine if product is included in promoted campaign.
     * @returns {Boolean}
     */
    display() {
      if (!this.graduatedPercentage) {
        this.checkVariantApplied();
      }

      if (this.graduatedPercentage.length > 0 && this.activeCampaign) {
        return this.activeCampaign && !this.promotedCampaign;
      }

      return this.promotedCampaign && this.discount;
    },

    /**
     * Map Pinia actions.
     */
    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
    }),

    /**
     * Get promoted campaign title.
     * @param {Boolean} [boldClasses] - Classes to apply to <strong> elements.
     * @returns {String}
     */
    getPromotedCampaignTitle(boldClasses) {
      const string = this.$string(
        'discount_campaigns.promoted_campaign.title_html',
        {
          // Decoding '50%' throws an error
          decode: false,
          replace: {
            discount: `${this.discount}%`,
          },
        },
      );

      return boldClasses
        ? string?.replace(
            '<strong',
            '<strong class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"',
          )
        : string;
    },

    /**
     * Handle copy click.
     * @param {Boolean} modal - Handle copy in modal?
     */
    async handleCopy(modal) {
      navigator.clipboard.writeText(this.promotedCampaignCode);

      if (modal) {
        this.showModalTooltip = true;
      } else {
        this.showTooltip = true;
      }

      await cnvs.Sleep(this.$timing('message'));
      this.showTooltip = false;
      this.showModalTooltip = false;
    },

    /**
     * Handle open overlay.
     */
    handleOpenOverlay() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'discountCampaignsPromotedModal',
      });

      this.showModalTooltip = false;
    },

    /**
     * Handle close overlay.
     */
    handleCloseOverlay() {
      this.closeOverlay('discountCampaignsPromotedModal');
      this.showModalTooltip = false;
    },

    /**
     * Handle add to cart.
     */
    handleAddToCart() {
      this.$emit('add-to-cart');
      this.handleCloseOverlay();
    },

    /**
     * Handle graduated discount when no promotion campaign.
     * create an array for the template to loop through
     * @returns {Array}
     */
    handleGraduatedDiscountOnly() {
      const graduatedStrings = [];
      for (let index = 0; index < this.graduatedPercentage.length; index++) {
        const text = this.$string(
          'discount_campaigns.graduated_discount.from',
          {
            replace: {
              amount: this.graduatedQuantity[index],
            },
          },
        );
        graduatedStrings.push({
          text,
          percentage: `${this.graduatedPercentage[index]}%`,
        });
      }
      return graduatedStrings;
    },

    /**
     * Check if variant is part of the graduated discount
     */
    checkVariantApplied() {
      this.currentCampaign = this.promotedCampaign || this.activeCampaign;
      const graduated = this.currentCampaign?.graduatedDiscounts || [];
      graduated.forEach((tier) => {
        if (tier.variantIds.includes(this.variant.id.toString())) {
          this.graduatedPercentage.push(tier.threshold);
          this.graduatedQuantity.push(tier.quantity);
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import './product-promoted-campaign';
</style>
